import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Settings, 
  Users, 
  BarChart3, 
  Filter, 
  Search, 
  FileText, 
  Briefcase, 
  Shield,
  TrendingUp,
  DollarSign,
  Clock,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import RemotePrintSettings from './RemotePrintSettings';
import SheetBridgePanel from './admin/SheetBridgePanel';
import { MailboxAccessPanel } from './admin/MailboxAccessPanel';
import MasonPanel from './admin/MasonPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({});
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  
  // Filter states
  const [projectFilters, setProjectFilters] = useState({
    status: '',
    client_name: '',
    assigned_manager: '',
    date_from: '',
    date_to: ''
  });
  
  const [quoteFilters, setQuoteFilters] = useState({
    status: '',
    client_name: '',
    created_by: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    if (user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'project_manager_estimator') {
      fetchAdminData();
    }
  }, [user]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      // Fetch all admin data in parallel
      const [
        analyticsRes,
        usersRes,
        clientsRes,
        managersRes
      ] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/clients`),
        axios.get(`${API}/admin/project-managers`)
      ]);
      
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      setProjectManagers(managersRes.data);
      
      // Fetch initial projects and quotes
      await fetchFilteredProjects();
      await fetchFilteredQuotes();
      
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredProjects = async (filters = projectFilters) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`${API}/projects?${params}`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching filtered projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const fetchFilteredQuotes = async (filters = quoteFilters) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`${API}/quotes?${params}`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching filtered quotes:', error);
      toast.error('Failed to load quotes');
    }
  };

  const handleProjectFilterChange = (key, value) => {
    const newFilters = { ...projectFilters, [key]: value };
    setProjectFilters(newFilters);
    fetchFilteredProjects(newFilters);
  };

  const handleQuoteFilterChange = (key, value) => {
    const newFilters = { ...quoteFilters, [key]: value };
    setQuoteFilters(newFilters);
    fetchFilteredQuotes(newFilters);
  };

  const clearProjectFilters = () => {
    const emptyFilters = {
      status: '',
      client_name: '',
      assigned_manager: '',
      date_from: '',
      date_to: ''
    };
    setProjectFilters(emptyFilters);
    fetchFilteredProjects(emptyFilters);
  };

  const clearQuoteFilters = () => {
    const emptyFilters = {
      status: '',
      client_name: '',
      created_by: '',
      date_from: '',
      date_to: ''
    };
    setQuoteFilters(emptyFilters);
    fetchFilteredQuotes(emptyFilters);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      draft: 'bg-slate-200',
      sent: 'bg-blue-600',
      accepted: 'bg-green-600',
      rejected: 'bg-red-600',
      planning: 'bg-yellow-600',
      in_progress: 'bg-blue-600',
      completed: 'bg-green-600',
      on_hold: 'bg-orange-600',
      cancelled: 'bg-red-600'
    };
    return statusClasses[status] || 'bg-slate-200';
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin' && user?.role !== 'project_manager_estimator') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Settings className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600">Administrator or Project Manager access required</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Admin Panel</h1>
          <p className="text-slate-600">Complete system administration and analytics</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="skeleton h-4 w-20 mb-2"></div>
                <div className="skeleton h-8 w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header with Logo */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2 drop-shadow-lg">Admin Panel</h1>
          <p className="text-slate-600 font-semibold drop-shadow">Complete system administration and analytics</p>
        </div>
        <img 
          src="/365-logo-dark.png" 
          alt="365 Industrial Solutions" 
          className="h-16 w-auto opacity-90 drop-shadow-lg"
        />
      </div>

      {/* 🔥 FISCAL YEAR REVENUE - THE BIG DEAL 🔥 */}
      <Card className="mb-8 ti-panel border-0 overflow-hidden relative">
        
        <CardContent className="p-8 relative z-10">
          <div className="text-center mb-6">
            <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">
              365 INDUSTRIAL SOLUTIONS
            </h2>
            <p className="text-sky-800 text-lg font-semibold">Company-Wide Revenue Performance</p>
          </div>
          
          {(() => {
            const fys = Object.values(analytics.fiscal_year_breakdown || {}).sort((a, b) => Number(a.prefix) - Number(b.prefix)).slice(-3);
            const fmtD = (iso) => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
            return (
              <div className={`grid grid-cols-1 ${fys.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8 max-w-6xl mx-auto`} data-testid="fiscal-year-cards">
                {fys.map(fy => fy.is_current ? (
                  <div key={fy.year} className="bg-gradient-to-br from-green-50 to-emerald-50 backdrop-blur-xl rounded-2xl p-8 border-2 border-green-200 hover:border-green-300 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-green-500/20" data-testid={`fy-card-${fy.year}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="bg-green-50 rounded-full p-3 animate-pulse"><Activity className="h-8 w-8 text-slate-900" /></div>
                      <Badge className="bg-green-400 text-green-900 text-sm font-bold px-4 py-1 animate-pulse">CURRENT</Badge>
                    </div>
                    <h3 className="text-slate-900 text-lg font-bold mb-2">FISCAL YEAR {fy.year}</h3>
                    <p className="text-slate-600 text-sm mb-4">{fmtD(fy.start)} - {fmtD(fy.end)}</p>
                    <div className="space-y-2">
                      <p className="text-5xl lg:text-6xl font-black text-slate-900">{formatCurrency(fy.revenue || 0)}</p>
                      <p className="text-slate-600 text-sm font-semibold">{fy.projects || 0} Projects In Progress</p>
                    </div>
                  </div>
                ) : (
                  <div key={fy.year} className="bg-slate-900/5 backdrop-blur-xl rounded-2xl p-8 border border-slate-200 hover:bg-slate-900/10 transition-all duration-300 transform hover:scale-105" data-testid={`fy-card-${fy.year}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="bg-slate-900/10 rounded-full p-3"><TrendingUp className="h-8 w-8 text-slate-900" /></div>
                      <Badge className="bg-slate-900/10 text-slate-900 text-sm font-bold px-4 py-1">COMPLETE</Badge>
                    </div>
                    <h3 className="text-slate-600 text-lg font-bold mb-2">FISCAL YEAR {fy.year}</h3>
                    <p className="text-slate-600 text-sm mb-4">{fmtD(fy.start)} - {fmtD(fy.end)}</p>
                    <div className="space-y-2">
                      <p className="text-5xl lg:text-6xl font-black text-slate-900">{formatCurrency(fy.revenue || 0)}</p>
                      <p className="text-slate-600 text-sm font-semibold">{fy.projects || 0} Projects Completed</p>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          
          {/* TOTAL COMPANY REVENUE - THE BIGGEST DEAL */}
          <div className="mt-8 text-center bg-slate-900/5 backdrop-blur-xl rounded-2xl p-6 border border-slate-200">
            <p className="text-slate-600 text-sm font-bold uppercase tracking-wider mb-2">TOTAL COMPANY REVENUE (ALL TIME)</p>
            <p className="text-7xl font-black text-slate-900  mb-2">
              {formatCurrency(analytics.total_company_revenue || 0)}
            </p>
            <p className="text-slate-600 text-lg font-semibold">
              {Object.values(analytics.fiscal_year_breakdown || {}).reduce((n, fy) => n + (fy.projects || 0), 0)} Total Projects
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Overview - Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Users</p>
                <p className="text-2xl font-bold text-blue-700">{analytics.total_users || 0}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Active Projects</p>
                <p className="text-2xl font-bold text-orange-700">{analytics.project_stats?.in_progress || 0}</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover-lift">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Pending Quotes</p>
                <p className="text-2xl font-bold text-yellow-700">{analytics.quote_stats?.sent || 0}</p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <RemotePrintSettings canEdit={['super_admin', 'office_admin'].includes(user?.role)} />
      <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') || 'projects'} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 bg-slate-900/10 backdrop-blur-sm">
          <TabsTrigger value="projects" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">Projects</TabsTrigger>
          <TabsTrigger value="quotes" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">Quotes</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">Users</TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60">Analytics</TabsTrigger>
          <TabsTrigger value="sheets" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60" data-testid="admin-tab-sheets">Sheets & Fiscal Books</TabsTrigger>
          <TabsTrigger value="mason" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:text-slate-800 data-[state=inactive]:bg-white/60" data-testid="admin-tab-mason">MASON</TabsTrigger>
        </TabsList>

        <TabsContent value="mason">
          <MasonPanel />
        </TabsContent>

        <TabsContent value="sheets">
          <SheetBridgePanel user={user} />
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <Card className="card">
            <CardHeader className="card-header border-b border-slate-200">                        
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-900 flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-blue-500" />
                  Project Management ({projects.length})
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearProjectFilters}
                  className="bg-white/60 hover:bg-white/80 text-slate-800 border-slate-200"
                >
                  Clear Filters
                </Button>
              </div>
              
              {/* Project Filters */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                <div>
                  <Label className="text-slate-700">Status</Label>
                  <select
                    value={projectFilters.status}
                    onChange={(e) => handleProjectFilterChange('status', e.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-md text-slate-900 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="planning">Planning</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                
                <div>
                  <Label className="text-slate-700">Client</Label>
                  <Input
                    placeholder="Client name..."
                    value={projectFilters.client_name}
                    onChange={(e) => handleProjectFilterChange('client_name', e.target.value)}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">Project Manager</Label>
                  <select
                    value={projectFilters.assigned_manager}
                    onChange={(e) => handleProjectFilterChange('assigned_manager', e.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-md text-slate-900 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Managers</option>
                    {projectManagers.map(manager => (
                      <option key={manager.id} value={manager.id}>{manager.full_name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label className="text-slate-700">From Date</Label>
                  <Input
                    type="date"
                    value={projectFilters.date_from}
                    onChange={(e) => handleProjectFilterChange('date_from', e.target.value)}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">To Date</Label>
                  <Input
                    type="date"
                    value={projectFilters.date_to}
                    onChange={(e) => handleProjectFilterChange('date_to', e.target.value)}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="card-body">
              <div className="space-y-4">
                {projects.map((project) => (
                  <div key={project.id} className="p-4 bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200 hover:bg-white/70 transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{project.title}</h4>
                      <Badge className={`${getStatusBadgeClass(project.status)} text-slate-900`}>
                        {project.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-700">
                      <div>
                        <span className="text-slate-500">Client:</span> {project.client_name}
                      </div>
                      <div>
                        <span className="text-slate-500">Budget:</span> {formatCurrency(project.estimated_budget)}
                      </div>
                      <div>
                        <span className="text-slate-500">Created:</span> {formatDate(project.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes">
          <Card className="card">
            <CardHeader className="card-header border-b border-slate-200">
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-500" />
                  Quote Management ({quotes.length})
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearQuoteFilters}
                  className="bg-white/60 hover:bg-white/80 text-slate-800 border-slate-200"
                >
                  Clear Filters
                </Button>
              </div>
              
              {/* Quote Filters */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                <div>
                  <Label className="text-slate-700">Status</Label>
                  <select
                    value={quoteFilters.status}
                    onChange={(e) => handleQuoteFilterChange('status', e.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-md text-slate-900 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                
                <div>
                  <Label className="text-slate-700">Client</Label>
                  <Input
                    placeholder="Client name..."
                    value={quoteFilters.client_name}
                    onChange={(e) => handleQuoteFilterChange('client_name', e.target.value)}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">Created By</Label>
                  <select
                    value={quoteFilters.created_by}
                    onChange={(e) => handleQuoteFilterChange('created_by', e.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-md text-slate-900 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Users</option>
                    {users.filter(u => u.role === 'project_manager_estimator').map(user => (
                      <option key={user.id} value={user.id}>{user.full_name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label className="text-slate-700">From Date</Label>
                  <Input
                    type="date"
                    value={quoteFilters.date_from}
                    onChange={(e) => handleQuoteFilterChange('date_from', e.target.value)}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">To Date</Label>
                  <Input
                    type="date"
                    value={quoteFilters.date_to}
                    onChange={(e) => handleQuoteFilterChange('date_to', e.target.value)}
                    className="bg-white/80 backdrop-blur-sm border-slate-200 text-slate-900"
                  />
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="card-body">
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <div key={quote.id} className="p-4 bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200 hover:bg-white/70 transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">{quote.project_title}</h4>
                      <Badge className={`${getStatusBadgeClass(quote.status)} text-slate-900`}>
                        {quote.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-700">
                      <div>
                        <span className="text-slate-500">Client:</span> {quote.client_name}
                      </div>
                      <div>
                        <span className="text-slate-500">Amount:</span> {formatCurrency(quote.estimated_cost)}
                      </div>
                      <div>
                        <span className="text-slate-500">Created:</span> {formatDate(quote.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="card">
            <CardHeader className="card-header">
              <CardTitle className="text-slate-900 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-500" />
                User Management ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="card-body">
              <div className="space-y-4">
                {users.map((user) => (
                  <div key={user.id} className="p-4 bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200 hover:bg-white/70 transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">{user.full_name}</h4>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-blue-600 text-white mb-1">
                          {user.role.replace('_', ' ')}
                        </Badge>
                        <p className="text-xs text-slate-500">
                          Joined {formatDate(user.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <MailboxAccessPanel />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          {/* Department Revenue Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
            <Card className="card hover-lift border-green-200 bg-gradient-to-br from-green-50 to-slate-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-slate-500 text-sm font-medium">Justin Field Work</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(analytics.department_breakdown?.justin_field_work?.revenue || 0)}</p>
                  <p className="text-xs text-slate-500">{analytics.department_breakdown?.justin_field_work?.projects || 0} projects</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card hover-lift border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-slate-500 text-sm font-medium">Matt Millwright</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(analytics.department_breakdown?.matt_millwright?.revenue || 0)}</p>
                  <p className="text-xs text-slate-500">{analytics.department_breakdown?.matt_millwright?.projects || 0} projects</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card hover-lift border-orange-200 bg-gradient-to-br from-orange-50 to-slate-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-slate-500 text-sm font-medium">Jeff D Fabrication</p>
                  <p className="text-2xl font-bold text-orange-700">{formatCurrency(analytics.department_breakdown?.jeff_dickson_fabrication?.revenue || 0)}</p>
                  <p className="text-xs text-slate-500">{analytics.department_breakdown?.jeff_dickson_fabrication?.projects || 0} projects</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card hover-lift border-purple-200 bg-gradient-to-br from-purple-50 to-slate-50">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-slate-500 text-sm font-medium">Jeff H Service</p>
                  <p className="text-2xl font-bold text-purple-700">{formatCurrency(analytics.department_breakdown?.jeff_henhoeffer_service?.revenue || 0)}</p>
                  <p className="text-xs text-slate-500">{analytics.department_breakdown?.jeff_henhoeffer_service?.projects || 0} projects</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <Card className="card hover-lift border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm font-medium">Total Project Revenue</p>
                    <p className="text-3xl font-bold text-green-700">{formatCurrency(analytics.total_project_revenue || 0)}</p>
                  </div>
                  <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card hover-lift border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm font-medium">Completed Revenue</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(analytics.total_completed_revenue || 0)}</p>
                  </div>
                  <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card hover-lift border-yellow-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm font-medium">In Progress Revenue</p>
                    <p className="text-2xl font-bold text-amber-700">{formatCurrency(analytics.total_in_progress_revenue || 0)}</p>
                  </div>
                  <div className="h-12 w-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Statistics */}
            <Card className="card">
              <CardHeader className="card-header">
                <CardTitle className="text-slate-900 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-blue-500" />
                  Users by Role
                </CardTitle>
              </CardHeader>
              <CardContent className="card-body">
                <div className="space-y-3">
                  {Object.entries(analytics.user_stats || {}).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-slate-700 capitalize">{role.replace('_', ' ')}</span>
                      <Badge variant="outline" className="border-slate-300 text-slate-700">
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Project Statistics */}
            <Card className="card">
              <CardHeader className="card-header">
                <CardTitle className="text-slate-900 flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-blue-500" />
                  Projects by Status
                </CardTitle>
              </CardHeader>
              <CardContent className="card-body">
                <div className="space-y-3">
                  {Object.entries(analytics.project_stats || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-slate-700 capitalize">{status.replace('_', ' ')}</span>
                      <Badge 
                        className={`${getStatusBadgeClass(status)} text-slate-900`}
                      >
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quote Statistics */}
            <Card className="card">
              <CardHeader className="card-header">
                <CardTitle className="text-slate-900 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-500" />
                  Quotes by Status
                </CardTitle>
              </CardHeader>
              <CardContent className="card-body">
                <div className="space-y-3">
                  {Object.entries(analytics.quote_stats || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-slate-700 capitalize">{status}</span>
                      <Badge 
                        className={`${getStatusBadgeClass(status)} text-slate-900`}
                      >
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Safety Statistics */}
            <Card className="card">
              <CardHeader className="card-header">
                <CardTitle className="text-slate-900 flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-blue-500" />
                  Safety Tickets
                </CardTitle>
              </CardHeader>
              <CardContent className="card-body">
                <div className="space-y-3">
                  {Object.entries(analytics.safety_stats || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-slate-700 capitalize">{status.replace('_', ' ')}</span>
                      <Badge variant="outline" className="border-slate-300 text-slate-700">
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;